<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { NButton, NEmpty, NInput, NPopconfirm, NSpin, NTime, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import ApiTokenRevealModal from './ApiTokenRevealModal.vue'
import { useApiTokensStore } from '../stores/api_tokens'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only page for managing `ApiToken` rows that authenticate the
// `brain` CLI, the Discord bot, and any other bearer-auth client.
// Auth gating happens at the router level (see router/index.ts) —
// non-admins are redirected to / before this view mounts.

const store = useApiTokensStore()
const message = useMessage()

const name = ref('')
const submitting = ref(false)
const revealOpen = ref(false)

useDocumentTitle('API tokens')

// Always refetch on mount — unlike LintReview / SynthesisReview which
// receive live ActionCable pushes for the fields they care about, the
// only thing that updates `last_used_at` on an ApiToken row is a
// bearer-authenticated request from OUTSIDE the SPA (CLI, MCP, Discord
// bot). The Pinia store survives navigation, so a cache-on-first-fetch
// `if (!store.loaded)` guard would leave the list showing "never used"
// indefinitely for tokens that the user IS using outside the browser
// `loaded` is sticky — it never resets to false — so the
// background refetch doesn't briefly flash the spinner.
onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    // loadError surfaces in the template; nothing to do here.
  }
})

async function submit() {
  const trimmed = name.value.trim()
  if (!trimmed) {
    message.error('Name is required')
    return
  }
  submitting.value = true
  try {
    await store.create(trimmed)
    name.value = ''
    revealOpen.value = true
  } catch (e) {
    const reason = e instanceof Error ? e.message : 'Failed to create token'
    message.error(reason)
  } finally {
    submitting.value = false
  }
}

async function revoke(id: number) {
  try {
    await store.destroy(id)
    message.success('Token revoked')
  } catch (e) {
    const reason = e instanceof Error ? e.message : 'Failed to revoke token'
    message.error(reason)
  }
}
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="page-title">API tokens</h1>
        <p class="text-sm text-white/60 m-0">
          Long-lived bearer tokens for the
          <code class="font-mono text-xs">brain</code> CLI, the Discord bot, and any other client that authenticates
          with <code class="font-mono text-xs">Authorization: Bearer …</code>. The cleartext value is shown exactly once
          — copy it as soon as it appears.
        </p>
      </header>

      <form class="flex gap-2 items-end" @submit.prevent="submit">
        <div class="flex flex-col gap-1 flex-1">
          <label for="api-token-name" class="text-xs text-white/60">
            Name (e.g. <code class="font-mono">cli-laptop</code>)
          </label>
          <NInput
            id="api-token-name"
            v-model:value="name"
            placeholder="cli-laptop"
            :disabled="submitting"
            data-testid="api-token-name-input"
            @keydown.enter.prevent="submit"
          />
        </div>
        <NButton
          type="primary"
          :loading="submitting"
          :disabled="!name.trim()"
          data-testid="api-token-create"
          @click="submit"
        >
          Create token
        </NButton>
      </form>

      <div v-if="!store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>
      <NEmpty v-else-if="store.tokens.length === 0" description="No API tokens yet. Create one above." />
      <ul v-else class="flex flex-col gap-2 list-none p-0 m-0" data-testid="api-token-list">
        <li
          v-for="token in store.tokens"
          :key="token.id"
          class="flex items-center justify-between gap-3 rounded border border-white/10 bg-black/20 px-3 py-2"
        >
          <div class="flex flex-col gap-0.5 min-w-0">
            <div class="text-sm text-white font-medium truncate">{{ token.name }}</div>
            <div class="text-xs text-white/50 truncate">
              {{ token.owner_email }} ·
              <span v-if="token.last_used_at">
                last used <NTime :time="new Date(token.last_used_at)" type="relative" />
              </span>
              <span v-else>never used</span>
              · created <NTime :time="new Date(token.created_at)" type="relative" />
            </div>
          </div>
          <NPopconfirm @positive-click="revoke(token.id)">
            <template #trigger>
              <NButton size="small" tertiary type="error" :data-testid="`api-token-revoke-${token.id}`">
                Revoke
              </NButton>
            </template>
            Revoke this token? Any client using it will fail to authenticate on its next request.
          </NPopconfirm>
        </li>
      </ul>
    </section>

    <ApiTokenRevealModal v-model:show="revealOpen" />
  </RouteShell>
</template>
